"use client";

import { Card, StepLabel } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Step from "@mui/material/Step";
import Stepper from "@mui/material/Stepper";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import PageContainer from "@/components/atoms/PageContainer";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useTabsOrientation from "@/hooks/use-tabs-orientation";
import { LojaIntegradaConnectionMessages } from "@/messages/platforms/lojaIntegradaConnection/lojaIntegrada-connection.messages";
import { BreadCrumb } from "@/types/page.types";

import { LojaIntegradaAccountConnection } from "./LojaIntegradaAccountConnection";
import { LojaIntegradaCompletion } from "./LojaIntegradaCompletion";
import { LojaIntegradaOverview } from "./LojaIntegradaOverview";

type LojaIntegradaConnectionProps = {
  messages: LojaIntegradaConnectionMessages;
};

export function LojaIntegradaConnection({ messages }: LojaIntegradaConnectionProps) {
  const [activeStep, setActiveStep] = useState(0);
  const router = useNavigate();
  const { lang } = useLanguage();
  const orientation = useTabsOrientation();

  const completeConnection = () => {
    router(ROUTE_URLS.platform(lang));
  };

  const handleNext = () => {
    if (isLastStep()) return;
    setActiveStep((prevStep) => prevStep + 1);
  };

  const BREAD_CRUMBS: BreadCrumb[] = [
    { title: messages.breadCrumbTitle, href: ROUTE_URLS.platform(lang) },
  ];

  const steps = [
    {
      label: messages.overviewStepTitle,
      content: <LojaIntegradaOverview messages={messages} />,
    },
    {
      label: messages.accountConnectionStepTitle,
      content: (
        <LojaIntegradaAccountConnection
          messages={messages}
          onCompleteLojaIntegradaSubscription={handleNext}
        />
      ),
    },
    {
      label: messages.completionStepTitle,
      content: <LojaIntegradaCompletion messages={messages} />,
    },
  ];

  const totalSteps = steps.length;

  const isLastStep = () => activeStep === totalSteps - 1;

  return (
    <PageLayout title={messages.pageTitle} messages={messages} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <MDBox mt={5} mb={9}>
          <Grid container justifyContent="center">
            <Grid size={12}>
              <Card>
                <MDBox mt={-3} mb={3} mx={2}>
                  <Stepper orientation={orientation} activeStep={activeStep} alternativeLabel>
                    {steps.map((step) => (
                      <Step key={step.label}>
                        <StepLabel>{step.label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    {steps[activeStep].content}
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      <MDBox />
                      {activeStep === 1 ? (
                        <MDBox />
                      ) : (
                        <MDButton
                          variant="gradient"
                          color="dark"
                          onClick={!isLastStep() ? handleNext : completeConnection}
                        >
                          {isLastStep() ? messages.completeButton : messages.nextButton}
                        </MDButton>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </PageContainer>
    </PageLayout>
  );
}
